/* eslint-disable no-param-reassign */
import React, { useCallback, useMemo, useState } from "react";
import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { createContext } from "use-context-selector";

import { Channel } from "stream-chat";
import {
  DeleteProposalLineItemMutationResult,
  DeleteProposalLineItemMutationVariables,
  DeleteProposalLineItemQuery,
  GetMyProposalsQuery,
  GetMyProposalsQueryResult,
  GetMyProposalsQueryVariables,
  MarketplaceOrderStatus,
  MarketplaceProposal,
  MarketplaceProposalStatus,
  TransitionOrderQuery,
  TransitionProposalQuery,
  UpsertProposalQuery,
  UpsertProposalMutationResult,
  UpsertProposalMutationVariables,
  UpsertProposalLineItemMutationResult,
  UpsertProposalLineItemMutationVariables,
  UpsertProposalLineItemQuery,
  UpsertProposalMetaItemQuery,
  MarketplaceInvoiceStatus,
  TransitionMarketplaceInvoiceQuery,
} from "../../../schema/generated/schema";

import {
  JsonSchemaActionContext,
  JsonSchemaActionContextValue,
  JsonSchemaDataContextProvider,
  JsonSchemaLineItemInput,
} from "../../JsonSchemaLoftTheme/JsonSchemaDataContext";
import { FileContextProvider } from "./fileContext";
import fileUtils, { FileShareEntityType } from "../../../utils/fileUtils";

interface ProposalContextData {
  getProposal: {
    proposal: MarketplaceProposal | undefined;
    loading: boolean;
    error: ApolloError | undefined;
    called: boolean;
  };
  chatChannel: Channel | undefined;
}

interface ProposalContextActions {
  handleSetChatChannel(channel: Channel): void;
  refetch: () => void;
  transitionOrder(status: MarketplaceOrderStatus): Promise<void>;
  transitionMarketplaceInvoice(
    invoiceId: string,
    status: MarketplaceInvoiceStatus
  ): Promise<void>;
  transitionProposal(status: MarketplaceProposalStatus): Promise<void>;
}

interface ProposalContextStore {
  data: ProposalContextData;
  actions: ProposalContextActions;
}

const ProposalContext = createContext<Partial<ProposalContextStore>>({});

type ProposalProviderProps = {
  children: React.ReactChild;
};
export type Properties = { [key: string]: { [property: string]: any } };

export const ProposalProvider = ({
  children,
}: ProposalProviderProps): React.ReactElement => {
  const [chatChannel, setChatChannel] = useState();
  const { referenceId } = useParams<{ referenceId: string }>();

  const proposalQuery = useQuery<
    GetMyProposalsQueryResult,
    GetMyProposalsQueryVariables
  >(GetMyProposalsQuery, {
    skip: !referenceId,
    fetchPolicy: "cache-first",
    variables: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      proposalIds: [referenceId!],
    },
  });

  const proposal = proposalQuery.data?.getMyProposals.find(p => {
    return p.id === referenceId || p.refId === referenceId;
  }) as MarketplaceProposal;

  const [upsertProposalMetaItem, upsertProposalMetaResult] = useMutation(
    UpsertProposalMetaItemQuery
  );

  const [upsertProposalLineItem, upsertProposalLineItemResult] = useMutation<
    UpsertProposalLineItemMutationResult,
    UpsertProposalLineItemMutationVariables
  >(UpsertProposalLineItemQuery);

  const [deleteProposalLineItem, deleteProposalLineItemResult] = useMutation<
    DeleteProposalLineItemMutationResult,
    DeleteProposalLineItemMutationVariables
  >(DeleteProposalLineItemQuery);

  const [transitionProposal, { loading: transitionLoading }] = useMutation(
    TransitionProposalQuery,
    {
      onCompleted() {
        proposalQuery.refetch?.();
      },
    }
  );

  const [transitionOrder, { loading: orderLoading }] = useMutation(
    TransitionOrderQuery,
    {
      onCompleted() {
        proposalQuery.refetch?.();
      },
    }
  );

  const [transitionMarketplaceInvoice] = useMutation(
    TransitionMarketplaceInvoiceQuery,
    {
      onCompleted() {
        proposalQuery.refetch?.();
      },
    }
  );

  const [upsertProposal, upsertProposalResult] = useMutation<
    UpsertProposalMutationResult,
    UpsertProposalMutationVariables
  >(UpsertProposalQuery);

  const loading = useMemo(
    () => proposalQuery.called && proposalQuery.loading,
    [proposalQuery.called, proposalQuery.loading]
  );

  const proposalId = proposal?.id || "";

  const transitionProposalAction = useCallback(
    async (status: MarketplaceProposalStatus) =>
      new Promise<void>((resolve, reject) => {
        transitionProposal({
          variables: {
            proposalId,
            status,
          },
        })
          .then(() => resolve())
          .catch(reject);
      }),
    [referenceId, !!proposal]
  );

  const transitionOrderAction = useCallback(
    async (status: MarketplaceOrderStatus) => {
      const orderId = proposal?.order?.id;
      if (!orderId) {
        console.error("no order on proposal");
        return;
      }
      await transitionOrder({
        variables: { orderId, status },
      });
    },
    [!!proposal]
  );

  const transitionMarketplaceInvoiceAction = useCallback(
    async (invoiceId: string, status: MarketplaceInvoiceStatus) => {
      await transitionMarketplaceInvoice({
        variables: { invoiceId, status },
      });
    },
    [!!proposal]
  );

  const handleLineItemDelete = (itemId: string) => {
    return deleteProposalLineItem({
      variables: {
        proposalId,
        itemId,
      },
    }).then(() => ({
      ...deleteProposalLineItemResult,
      data: deleteProposalLineItemResult.data?.deleteProposalLineItem,
    }));
  };

  const handleLineItemUpsert = (lineItem: JsonSchemaLineItemInput) => {
    return upsertProposalLineItem({
      variables: {
        proposalId,
        item: {
          ...lineItem,
          quantity: lineItem.quantity || 0,
        },
      },
    }).then(() => ({
      ...upsertProposalLineItemResult,
      data: upsertProposalLineItemResult.data?.upsertProposalLineItem,
    }));
  };

  const refetch = useCallback(() => {
    proposalQuery.refetch?.();
  }, [proposalQuery.refetch]);

  const handleSetChatChannel = useCallback(chatChannel => {
    setChatChannel(chatChannel);
  }, []);

  const value: ProposalContextStore = {
    data: {
      getProposal: {
        proposal,
        loading,
        error: proposalQuery.error,
        called: proposalQuery.called,
      },
      chatChannel,
    },
    actions: {
      transitionProposal: transitionProposalAction,
      transitionOrder: transitionOrderAction,
      transitionMarketplaceInvoice: transitionMarketplaceInvoiceAction,
      handleSetChatChannel,
      refetch,
    },
  };

  const fileShareEntity = {
    type: FileShareEntityType.PROPOSAL,
    id: proposalId,
  };

  const proposalFormValue: JsonSchemaActionContextValue = {
    proposal,
    isSaving:
      upsertProposalResult.loading ||
      upsertProposalMetaResult.loading ||
      transitionLoading ||
      orderLoading,
    actions: {
      refetch,
      handleLineItemDelete,
      handleLineItemUpsert,
      async handleSaveOverview(data) {
        if (!proposal) {
          throw new Error("invalid state");
        }
        const { subject, ...details } = data;
        const result = await upsertProposal({
          errorPolicy: "all",
          variables: {
            input: {
              id: proposal.id,
              templateSlug: proposal.proposalTemplate?.slug || "",
              buyerUserId: proposal.buyerUserId,
              sellerPartnerId: proposal.sellerPartnerId,
              subject,
              details,
            },
          },
        });
        return {
          ...upsertProposalResult,
          ...result,
        };
      },
      async handleMetaItemUpsert(formVersionId, formData) {
        const result = await upsertProposalMetaItem({
          errorPolicy: "all",
          variables: {
            proposalId,
            item: {
              formVersionId,
              formData,
            },
          },
        });
        return {
          ...upsertProposalMetaResult,
          ...result,
        };
      },
    },
  };

  return (
    <ProposalContext.Provider value={value}>
      <FileContextProvider
        defaultShareEntity={fileShareEntity}
        entities={
          proposal
            ? fileUtils.getEntitiesFromProposal(proposal)
            : [fileShareEntity]
        }
      >
        <JsonSchemaActionContext.Provider value={proposalFormValue}>
          <JsonSchemaDataContextProvider>
            {children}
          </JsonSchemaDataContextProvider>
        </JsonSchemaActionContext.Provider>
      </FileContextProvider>
    </ProposalContext.Provider>
  );
};

export default ProposalContext;
